import localForage from 'localforage';
import i18n from 'i18n';
import _ from '@lodash';
import { alpha } from '@mui/material/styles';
import themesConfig from 'app/fuse-configs/themesConfig';
import { persistReducer } from 'redux-persist';
import { type RootState } from 'app/store';
import {
	type ProfileState,
	type ProfileActionTypes,
	type UserPreferences,
	languages,
	type SignatureField,
	type Profile,
	userPreferencesMessagesNames,
} from 'app/store/types';
import { getSelectedLicenseGroupId, getSiteBackgroundCache } from 'app/store/reducers/app.reducer';
import { createSelector } from '@reduxjs/toolkit';
import { getRootDomain, getSiteBackgroundImageWithFallbacks } from 'app/utils/helpers';
import { licenseGroupPlanDesignCheck } from 'app/utils/tenant-plan';
import { objectFromEntries } from 'ts-extras';

const initialState: ProfileState = {
	info: undefined!, // HACK::assume never undefined and handle check only in <LicenseGroupWrapper /> and <ToolbarLayoutAdmin />
	previousProfileId: undefined,
};

const profileReducer = (state = initialState, action: ProfileActionTypes) => {
	switch (action.type) {
		case 'GET_PROFILE_SUCCESS': {
			const { data } = action.payload;
			return {
				...state,
				info: data?.user,
				previousProfileId: data?.user?.id ?? undefined,
			};
		}
		case 'GET_SIGNATURE_SUCCESS': {
			const { data } = action.payload;
			return {
				...state,
				info: {
					...state.info,
					signature: { ...data.signature },
				},
			};
		}
		case 'GET_UNIV_PRINT_PROFILE_SUCCESS': {
			const { data } = action.payload;
			return {
				...state,
				info: {
					...state.info,
					univPrintProfile: { ...data.univPrintProfile },
				},
			};
		}
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'profile',
		storage: localForage,
		whitelist: ['info', 'previousProfileId'],
	},
	profileReducer,
);

const currentLanguage = (
	languages.includes(i18n.language.slice(0, 2) as any) ? i18n.language.slice(0, 2) : 'en'
) as UserPreferences['language'];

export const defaultUserPreferencesStarter: UserPreferences = {
	startPage: 'workflows',
	homePageLayout: 'rows',
	quickLinks: [undefined, undefined, undefined],
	appearance: 'light',
	siteBackground: 'colors/white.svg',
	tableHeaderBackground: 'standard',
	tableHeaderStandardBackgroundOpacity: 100,
	tableHeaderColorBackgroundOpacity: 100,
	pageHeaderBackgroundOpacity: 100,
	navbarBackgroundOpacity: 100,
	messageRetentionDaysInfo: 7,
	messageRetentionDaysError: 7,
	messageRetentionDaysJob: 7,
	messageRetentionDaysOther: 7,
	language: currentLanguage,
	...objectFromEntries(userPreferencesMessagesNames.map((name) => [`${name}_email`, true])),
	...objectFromEntries(userPreferencesMessagesNames.map((name) => [`${name}_inbox`, true])),
};
export const defaultUserPreferencesEnterprise: UserPreferences = {
	startPage: 'home',
	homePageLayout: 'rows',
	quickLinks: [undefined, undefined, undefined],
	appearance: 'light',
	siteBackground: 'tropical-image.jpg',
	tableHeaderBackground: 'color',
	tableHeaderStandardBackgroundOpacity: 80,
	tableHeaderColorBackgroundOpacity: 80,
	pageHeaderBackgroundOpacity: 50,
	navbarBackgroundOpacity: 50,
	messageRetentionDaysInfo: 7,
	messageRetentionDaysError: 7,
	messageRetentionDaysJob: 7,
	messageRetentionDaysOther: 7,
	language: currentLanguage,
	...objectFromEntries(userPreferencesMessagesNames.map((name) => [`${name}_email`, true])),
	...objectFromEntries(userPreferencesMessagesNames.map((name) => [`${name}_inbox`, true])),
};

const getDefaultSignatureProfile = (profileInfo: Profile): SignatureField => {
	return {
		signatureActive: false,
		signatureImage: '',
		signatureType: 'typeSign',
		signatureText: `${profileInfo.firstName} ${profileInfo.lastName}`,
	};
};

const getUserPreferencesValuesOrDefaults = (preferences: UserPreferences | undefined) => {
	return {
		...(licenseGroupPlanDesignCheck('stratus') ? defaultUserPreferencesEnterprise : defaultUserPreferencesStarter),
		...(preferences ?? {}),
	};
};

const getUserSignatureValuesOrDefaults = (profile: Profile, signature?: SignatureField) => {
	return !_.isEmpty(signature) ? signature : getDefaultSignatureProfile(profile);
};

export const calculatedUserPreferences =
	({
		licenseGroupId,
		profileId,
		siteBackgroundCache,
	}: {
		licenseGroupId: string;
		profileId: string;
		siteBackgroundCache: number;
	}) =>
	(preferences: UserPreferences) => {
		const appearance =
			preferences.appearance === 'system'
				? window.matchMedia('(prefers-color-scheme: dark)').matches
					? 'dark'
					: 'light'
				: preferences.appearance;

		const lightBackgroundColor = themesConfig.default.palette.background.paper;
		const darkBackgroundColor = themesConfig.defaultDark.palette.background.paper;
		const backgroundColor = appearance === 'dark' ? darkBackgroundColor : lightBackgroundColor;

		const siteBackgroundImageWithFallbacks = getSiteBackgroundImageWithFallbacks({
			licenseGroupId,
			profileId,
			siteBackgroundCache,
			appearance,
		});

		const primarySiteBackgroundUrl =
			preferences.siteBackground === '%CUSTOM%'
				? `https://${getRootDomain()}${siteBackgroundImageWithFallbacks[0]}`
				: `${process.env.PUBLIC_URL}/assets/images/featured-backgrounds/${preferences.siteBackground}`;

		return {
			startPage: preferences.startPage,
			homePageLayout: preferences.homePageLayout,
			quickLinks: preferences.quickLinks.filter(
				(quickLink): quickLink is NonNullable<UserPreferences['quickLinks'][number]> => !!quickLink,
			),
			appearance,
			siteBackground:
				preferences.siteBackground === '%CUSTOM%'
					? siteBackgroundImageWithFallbacks
							.map((siteBackgroundImageUrl) => `url("${siteBackgroundImageUrl}")`)
							.join(', ')
					: `url("${process.env.PUBLIC_URL}/assets/images/featured-backgrounds/${
							preferences.siteBackground
						}"), url(${siteBackgroundImageWithFallbacks[siteBackgroundImageWithFallbacks.length - 1]})`,
			primarySiteBackgroundUrl,
			tableHeaderBackground: preferences.tableHeaderBackground,
			tableHeaderStandardBackgroundColor: alpha(
				backgroundColor,
				preferences.tableHeaderStandardBackgroundOpacity / 100,
			),
			tableHeaderColorBackgroundColor: {
				purple: alpha('#79679F', preferences.tableHeaderColorBackgroundOpacity / 100),
				green: alpha('#8DC567', preferences.tableHeaderColorBackgroundOpacity / 100),
				yellow: alpha('#FFE787', preferences.tableHeaderColorBackgroundOpacity / 100),
				blue: alpha('#A1C8FB', preferences.tableHeaderColorBackgroundOpacity / 100),
			},
			pageHeaderBackgroundColor: alpha(backgroundColor, preferences.pageHeaderBackgroundOpacity / 100),
			navbarBackgroundColor: alpha(backgroundColor, preferences.navbarBackgroundOpacity / 100),
			language: preferences.language,
		};
	};

export type TableHeaderColorBackgroundColorKey = keyof (ReturnType<typeof calculatedUserPreferences> extends (
	preferences: any,
) => infer R
	? R extends { tableHeaderColorBackgroundColor: infer T }
		? T
		: never
	: never);

export const getProfile = ({ profile }: RootState) => profile.info;
export const getUserPreferencesValues = ({ profile }: RootState) =>
	getUserPreferencesValuesOrDefaults(profile.info?.preferences);
export const getUserSignatureValues = ({ profile }: RootState) =>
	getUserSignatureValuesOrDefaults(profile.info, profile.info?.signature);
export const getUserPreferences = createSelector(
	[getUserPreferencesValues, getSelectedLicenseGroupId, getProfile, getSiteBackgroundCache],
	(preferencesValues, selectedLicenseGroupId, profile, siteBackgroundCache) => {
		return calculatedUserPreferences({
			licenseGroupId: selectedLicenseGroupId,
			profileId: profile?.id,
			siteBackgroundCache,
		})(preferencesValues);
	},
);

export type CalculatedUserPreferences = ReturnType<typeof getUserPreferences>;

export const getPreviousProfileId = ({ profile }: RootState) => profile.previousProfileId;
